/**
 * _moves.js — the MOVES every triage choice is built from (docs/ALDERFLOW.md §0b, Matt 2026-10-23:
 * "we're trying to teach the system how to make the triage cards so we can respond quickly to common
 * scenarios ... over time we'll have to do less and less training").
 *
 * A card is a SITUATION (a detector in api/alderflow.js that says when it applies and which facts it
 * found). Each choice on a card is a RECIPE: a short list of moves. A move is a plain object
 * { kind, ...params }; this file is the only place a move is worded, and services/alderflow.js#runMoves
 * the only place one is carried out. The laws (api/alderflow.js#applyLaws) read a choice through
 * `derived()` below, so a new recipe is checked like every other without new law code.
 *
 *   post       { milestones: [{ id, name, typeId }], dated: 'presented' | null }   post them, in order
 *   riders     { list: [{ threadId, title, from }], to: { id, name } }   each moves to `to` or is closed
 *   crew       { milestone: { id, name } | '$planned', members: [...] }  launch with this crew
 *   moveHours  { ids, hours, to: { id, name } | '$planned', basis: 'phase' | 'all' }   time entries onto it
 *   lineup     { milestones: [{ id, name }] | '$planned' }              into the Line-up
 *   plan       { phaseId, phaseName, template, count, first, anchorDate }   create a phase's milestones
 *   ask        { to: { id, name }, also: [ids], projectId, text, post?: { id, name } }   one Slack question
 *   note       { to: { id, name }, text, milestoneId? }                  a note in their triage tomorrow morning
 *   keyDate    { keyDateId, projectId, milestoneId, label, date }         set a project key date ('$date' = the date asked for)
 *   later      { cardId, days }                                          the card stays away that many days
 *   resolveNote { id, text }                                         a triage note is handled (database/346)
 *   target     { projectId, name, date, chainDays, bufferDays, basis, reason? }   set the project's Build Ready target ('$date' = the date asked for)
 *   qcAgree    { agreementId, decision: 'agree'|'decline' }             a director's answer on QC by agreement
 *   none       {}                                                       nothing changes today
 *
 * `$planned` means "the first milestone the plan move in the same choice creates".
 *
 * PURE: no I/O. Imported by api/alderflow.js only (the frontend runs the moves, it doesn't word them).
 */

const r1 = (x) => Math.round(x * 10) / 10
const first = (n) => String(n || '').split(/\s+/)[0]
export const listNames = (a) => a.length <= 1 ? a.join('') : a.slice(0, -1).join(', ') + ' and ' + a[a.length - 1]
const plural = (n, one, many) => (n === 1 ? one : many)
const cap = (s) => s ? s[0].toUpperCase() + s.slice(1) : s

/** Who, by first names, and the verb agreeing with them. */
export function crewWords(members) {
  const names = (members || []).map(m => first(m.memberName))
  return { who: listNames(names), one: names.length === 1, names }
}
const msName = (x, ctx) => x === '$planned' ? (ctx.plannedName || 'the first new milestone') : (x && x.name) || 'it'

/* ── how each move says itself: `short` for the tile (a clause), `lines` for the confirm step ── */
const SAY = {
  post(m) {
    const names = m.milestones.map((x, i) => {
      const prev = m.milestones[i - 1]
      return prev && /^client review/i.test(x.name) && x.name.toLowerCase().includes(String(prev.name).toLowerCase()) ? 'its client review' : x.name
    })
    const dated = m.dated === 'presented' ? `, dated the day ${m.milestones[0].name} was presented (you set the date in the next step)` : ''
    return {
      short: `post ${listNames(names)}`,
      lines: [{ t: `${cap(listNames(names))} ${plural(names.length, 'is', 'are')} marked posted${dated}.` },
        ...(m.milestones.length > 1 ? [{ t: 'Choose this only if each has really happened (for a client review: the client gave their feedback).', warn: true }] : [])],
    }
  },
  riders(m) {
    const n = m.list.length
    return {
      short: `${plural(n, 'its thread', `its ${n} threads`)} move to ${m.to.name} or close`,
      lines: [{ t: `${n === 1 ? 'One open thread is' : `${n} open threads are`} attached to ${m.list[0].from}. In the next step each one moves to ${m.to.name}, or you mark it handled.` }],
    }
  },
  crew(m, ctx) {
    const { who, one } = crewWords(m.members)
    const name = msName(m.milestone, ctx)
    return {
      short: `${who} ${one ? 'moves' : 'move'} onto ${name}`,
      lines: [{ t: `${who} ${one ? 'is' : 'are'} crewed on ${name}${one ? '' : `, with ${first(m.members[0] && m.members[0].memberName)} as captain`}. At Commit the app checks ${one ? 'they have' : 'each has'} a free slot (two milestones at most) and that ${name} has what it needs to start.` },
        ...m.members.map(x => ({ seat: x.memberAirtableId, name: first(x.memberName), seats: x.seats || [] }))],
    }
  },
  moveHours(m, ctx) {
    const name = msName(m.to, ctx)
    const stay = m.kept ? ' Hours already posted stay where they were logged.' : ''
    if (!m.ids || !m.ids.length) return m.kept
      ? { short: `the hours already posted stay where they were logged`, lines: [{ t: 'The hours were already posted, so they stay where they were logged. Nothing is re-filed.' }] }
      : { short: `none of the hours were logged to its phase, so none move`, lines: [{ t: `None of these hours were logged to ${name}’s phase, so none move onto it.${m.note ? ' ' + m.note : ''}` }] }
    const basis = m.basis === 'phase' ? ` logged to its phase` : ''
    return {
      short: `the ${r1(m.hours)} hours${basis}${m.kept ? ' not yet posted' : ''} move onto ${name}`,
      lines: [{ t: `The ${r1(m.hours)} hours${basis}${m.kept ? ' not yet posted' : ''} move onto ${name}.${m.note ? ' ' + m.note : ''}${stay}` }],
    }
  },
  lineup(m, ctx) {
    const names = m.milestones === '$planned' ? [msName('$planned', ctx)] : m.milestones.map(x => x.name)
    return {
      short: m.waitsFor ? `${listNames(names)} waits until ${m.waitsFor} posts` : `${listNames(names)} ${plural(names.length, 'goes', 'go')} in the Line-up to be crewed when someone is free`,
      lines: [{ t: m.waitsFor ? `${listNames(names)} ${plural(names.length, 'goes', 'go')} in the Line-up and waits there until ${m.waitsFor} posts, then is crewed in priority order.${m.after ? ' ' + m.after : ''}` : `${listNames(names)} ${plural(names.length, 'goes', 'go')} in the Line-up, the list of work ready to start. It is crewed in priority order when someone has a free slot.${m.after ? ' ' + m.after : ''}` }],
    }
  },
  plan(m) {
    return {
      short: `create the ${m.count} ${m.template.replace(/ Phase$/, '')} milestones in ${m.phaseName}`,
      lines: [{ t: `${m.count} milestones are created in ${m.phaseName} from the ${m.template} template, starting ${m.anchorLabel || 'today'}, first ${m.first}. They stay in this phase, so time and billing stay on it.` }],
    }
  },
  ask(m) {
    const who = listNames([first(m.to.name), ...(m.alsoNames || []).map(first)])
    return {
      short: m.short || `ask ${who}`,
      lines: [{ t: m.line || `${who} ${plural(1 + (m.alsoNames || []).length, 'is', 'are')} asked in Slack: “${m.text.length > 140 ? m.text.slice(0, 138) + '…' : m.text}”` }],
    }
  },
  resolveNote(m) {
    return { short: 'the note is handled', lines: [{ t: `The note “${String(m.text || '').slice(0, 80)}” is marked handled.` }] }
  },
  target(m) {
    const when = m.date && m.date !== '$date' ? new Date(m.date + 'T12:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }) : 'the date you enter'
    return { short: `${m.name} is held to Build Ready by ${when}`, lines: [{ t: `${m.name}’s Build Ready date is ${when}. It moves only by a decision recorded here, with the reason.` }] }
  },
  keyDate(m) {
    return { short: `the ${m.label.toLowerCase()} is saved`, lines: [{ t: `The ${m.label.toLowerCase()} is saved on the project; every milestone that shares it shows it.` }] }
  },
  qcAgree(m) {
    return m.decision === 'decline'
      ? { short: 'the sheets go through QC', lines: [{ t: 'You don’t agree, so the sheets go through QC as usual.' }] }
      : { short: 'you agree the sheets are QC approved', lines: [{ t: 'Your agreement is recorded. Once both directors agree, the sheets are QC approved, with the reason kept.' }] }
  },
  later(m) {
    return { short: `it asks again in ${m.days} day${m.days === 1 ? '' : 's'}`, lines: [{ t: `This card stays away for ${m.days} day${m.days === 1 ? '' : 's'}, then asks again.` }] }
  },
  note(m) {
    return { short: `${first(m.to.name)} gets a note tomorrow morning`, lines: [{ t: `${first(m.to.name)} gets a note in tomorrow’s triage: “${m.text}”` }] }
  },
  none(m) { return { short: m.short || 'nothing changes today', lines: [{ t: m.line || 'No change today. The card comes back tomorrow if nothing changes.' }] } },
}


/** The fields the laws and the screen read off a choice, whatever its recipe. */
export function derived(moves) {
  const crew = moves.find(m => m.kind === 'crew')
  const posts = moves.filter(m => m.kind === 'post').flatMap(m => m.milestones)
  const ask = moves.find(m => m.kind === 'ask')
  const riders = moves.find(m => m.kind === 'riders')
  const plan = moves.find(m => m.kind === 'plan')
  return {
    launches: !!crew,
    milestoneId: crew && crew.milestone !== '$planned' ? crew.milestone.id : null,
    milestoneName: crew ? (crew.milestone === '$planned' ? (plan && plan.first) : crew.milestone.name) : null,
    members: crew ? crew.members : [],
    posts,
    postMilestoneId: posts[0] ? posts[0].id : (ask && ask.post ? ask.post.id : null),
    postName: posts[0] ? posts[0].name : (ask && ask.post ? ask.post.name : null),
    riders: riders ? riders.list : undefined,
    asks: !!ask,
    memberName: ask ? ask.to.name : null,
    datedPosts: moves.some(m => m.kind === 'post' && m.dated),
    planned: !!plan,
  }
}

/**
 * A choice from a recipe. The situation names it (`l`, a short title like "Post Conceptual Framework")
 * and gives the moves; everything the person reads after that is written here from the moves.
 *   choice({ k, i, l, moves, primary?, caution?, go?, warn?: [lines], label?, sub? })
 * `label`/`sub` are for a choice INSIDE an option (the radio list), naming its distinct part.
 */
export function choice({ k, i, l, moves, primary = false, go, warn = [], extra = [], label, sub, title, done, story }) {
  const ctx = { plannedName: (moves.find(m => m.kind === 'plan') || {}).first }
  const said = moves.map(m => ({ m, ...SAY[m.kind](m, ctx) }))
  // The tile says the moves that change something, in order (a riders move is detail, for the confirm step).
  const shown = said.filter(x => x.m.kind !== 'riders' && x.m.kind !== 'none')
  const clauses = []
  const same = (a, b) => a && b && (a === b || (a.id && b.id && a.id === b.id))
  for (let n = 0; n < shown.length; n++) {
    const x = shown[n], nx = shown[n + 1]
    if (x.m.kind === 'crew' && nx && nx.m.kind === 'moveHours' && same(nx.m.to, x.m.milestone) && nx.m.ids && nx.m.ids.length) {
      clauses.push(`${x.short}, and ${x.m.members && x.m.members.length === 1 ? 'the' : 'their'} ${r1(nx.m.hours)} hours${nx.m.basis === 'phase' ? ' logged to its phase' : ''} go there too`); n++; continue
    }
    if (x.m.kind === 'moveHours' && nx && nx.m.kind === 'lineup' && Array.isArray(nx.m.milestones) && nx.m.milestones.length === 1 && same(nx.m.milestones[0], x.m.to)) {
      clauses.push(`${x.short}, which ${nx.m.waitsFor ? `waits until ${nx.m.waitsFor} posts` : 'waits in the Line-up until someone is free'}`); n++; continue
    }
    clauses.push(x.short)
  }
  // The tile carries the first two; the confirm step and the hover carry every move.
  const all = (clauses.length ? clauses : said.map(x => x.short))
  // Every clause, in order; the tile clamps to four lines and the hover shows it whole.
  const full = cap(all.join(', then '))
  const s = full
  const lines = [...warn.map(t => ({ t, warn: true })), ...said.flatMap(x => x.lines), ...extra.map(t => ({ t }))]
  return {
    k, i, l, s, primary, go: go || 'Choose this',
    title: title || l, whole: full, kn: lines, done: done || (full + '.'),
    // each step on its own line, for the commit screen
    steps: all.map(cap),
    label, sub,
    // the posts get a date in the confirm step (when it was presented)
    postsAsOf: moves.some(m => m.kind === 'post' && m.dated) || undefined,
    act: { type: 'moves', moves, ...derived(moves), ...(story ? { story } : null) },
    // kept so the laws can rebuild the choice with a move added (riders found aboard)
    recipe: { k, i, l, moves, primary, go, warn, extra, label, sub, title, done, story },
  }
}
